import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { CalendarClock, ClipboardCheck, ListChecks } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Page,
  Select,
  Sheet,
  TextArea,
  TextField,
  type DataTableColumn,
} from '@/components/ui'
import { useAcdmcOptions } from '@/hooks/useAcdmc'
import {
  useSaveTeachingLog,
  useTeachingDashboard,
  useTodaySchedule,
  useTopicCoverage,
  type TodaySlot,
  type TopicCoverageRow,
} from '@/services/acdmcTeachingService'
import { classLabel, classSelectOptions, errorMessage, sectionSelectOptions, todayKey } from '@/components/acdmc/AcdmcUi'
import '@/components/acdmc/acdmc.css'

const OUTCOME_OPTIONS = [
  { value: 'conducted', label: 'Conducted' },
  { value: 'partial_period', label: 'Partially conducted' },
  { value: 'not_conducted', label: 'Not conducted' },
  { value: 'supplementary', label: 'Supplementary / unplanned' },
]

const AcdmcTeaching: React.FC = () => {
  const today = todayKey()
  const { data: dashboard } = useTeachingDashboard()
  const { data: todayData, isLoading: todayLoading } = useTodaySchedule(today)
  const { data: options } = useAcdmcOptions()
  const saveLog = useSaveTeachingLog()

  const [className, setClassName] = useState('')
  const [section, setSection] = useState('')
  const [subjectName, setSubjectName] = useState('')
  const coverage = useTopicCoverage(className && subjectName ? { className, section, subjectName } : null)

  const [sheetOpen, setSheetOpen] = useState(false)
  const [activeSlot, setActiveSlot] = useState<TodaySlot | null>(null)
  const [periodOutcome, setPeriodOutcome] = useState('conducted')
  const [topicTitle, setTopicTitle] = useState('')
  const [remarks, setRemarks] = useState('')
  const [periodsActual, setPeriodsActual] = useState(1)

  const openLog = (slot: TodaySlot) => {
    setActiveSlot(slot)
    setPeriodOutcome(slot.teachingLog?.periodOutcome || 'conducted')
    setRemarks('')
    setTopicTitle('')
    setPeriodsActual(1)
    setSheetOpen(true)
  }

  const submitLog = async () => {
    if (!activeSlot) return
    try {
      const body = {
        date: activeSlot.date,
        className: activeSlot.className,
        section: activeSlot.section,
        subjectName: activeSlot.subjectName,
        scheduledTeacherName: activeSlot.scheduledTeacherName,
        periodOutcome,
        periodsScheduled: 1,
        periodsActual,
        remarks,
        timetableRef: {
          classRowId: activeSlot.classRowId,
          dayName: activeSlot.dayName,
          periodKey: activeSlot.periodKey,
          label: activeSlot.label,
        },
        lessonPlanId: activeSlot.lessonPlans[0]?._id,
        segments:
          periodOutcome === 'not_conducted'
            ? []
            : [
                {
                  title: topicTitle || 'Topic covered',
                  executionStatus: periodOutcome === 'partial_period' ? 'partial' : 'conducted',
                  topicCompletion: periodOutcome === 'partial_period' ? 'partial' : 'completed',
                  periodsCredited: periodsActual,
                  contentCovered: topicTitle,
                  remarks,
                },
              ],
        notConductedReason: periodOutcome === 'not_conducted' ? 'other' : undefined,
        notConductedDetail: periodOutcome === 'not_conducted' ? remarks : '',
      }
      if (activeSlot.teachingLog?._id) {
        await saveLog.mutateAsync({ id: activeSlot.teachingLog._id, body })
      } else {
        await saveLog.mutateAsync({ body })
      }
      toast.success('Teaching log saved')
      setSheetOpen(false)
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save teaching log'))
    }
  }

  const slotColumns = useMemo<DataTableColumn<TodaySlot>[]>(
    () => [
      { key: 'time', header: 'Period', render: (s) => s.label },
      { key: 'class', header: 'Class', render: (s) => classLabel(s.className, s.section) },
      { key: 'subject', header: 'Subject', render: (s) => s.subjectName },
      {
        key: 'plan',
        header: 'Lesson plan',
        render: (s) => (s.prepared ? <Badge tone="success">Ready</Badge> : <Badge tone="neutral">None</Badge>),
      },
      {
        key: 'log',
        header: 'Log',
        render: (s) =>
          s.teachingLog ? (
            <Badge tone="info">{s.teachingLog.periodOutcome}</Badge>
          ) : (
            <Badge tone="warning">Pending</Badge>
          ),
      },
      {
        key: 'action',
        header: '',
        render: (s) => (
          <Button size="sm" onClick={() => openLog(s)}>
            {s.teachingLog ? 'Update' : 'Record'}
          </Button>
        ),
      },
    ],
    [],
  )

  const coverageColumns = useMemo<DataTableColumn<TopicCoverageRow>[]>(
    () => [
      { key: 'topic', header: 'Topic', mobile: 'title', render: (r) => r.title },
      { key: 'planned', header: 'Planned', numeric: true, align: 'right', render: (r) => r.plannedPeriods },
      { key: 'actual', header: 'Actual', numeric: true, align: 'right', render: (r) => r.actualPeriods },
      { key: 'var', header: 'Variance', numeric: true, align: 'right', render: (r) => r.periodVariance },
      {
        key: 'status',
        header: 'Completion',
        render: (r) => (
          <Badge tone={r.completionStatus === 'completed' ? 'success' : r.completionStatus === 'partial' ? 'warning' : 'neutral'}>
            {r.completionStatus}
          </Badge>
        ),
      },
    ],
    [],
  )

  return (
    <Page
      title="Teaching tracker"
      description="Record what was actually taught against today's timetable. Lesson plan status and attendance are separate — this is syllabus execution evidence."
    >
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Today</div>
          <div className="text-lg font-semibold">{dashboard?.scheduledPeriodsToday ?? '—'} periods</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Logs pending</div>
          <div className="text-lg font-semibold">{dashboard?.missingTeachingLogs ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Open deviations</div>
          <div className="text-lg font-semibold">{dashboard?.openDeviations ?? 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground">Verified logs</div>
          <div className="text-lg font-semibold">{dashboard?.verifiedLogs ?? 0}</div>
        </Card>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <CalendarClock className="h-4 w-4" />
          Today&apos;s schedule (TMTBL)
        </div>
        {todayLoading && <p className="text-sm text-muted-foreground">Loading timetable…</p>}
        {!todayLoading && (todayData?.slots?.length ?? 0) === 0 ? (
          <EmptyState title="No scheduled periods" body="Publish a timetable for this session or check back on a school day." />
        ) : (
          <>
            <div className="hidden md:block">
              <DataTable columns={slotColumns} rows={todayData?.slots || []} getRowKey={(s) => `${s.classRowId}-${s.periodKey}`} />
            </div>
            <div className="md:hidden space-y-2">
              {(todayData?.slots || []).map((s) => (
                <Card key={`${s.classRowId}-${s.periodKey}`} className="p-3">
                  <div className="font-medium">
                    {s.label} · {classLabel(s.className, s.section)}
                  </div>
                  <div className="text-sm text-muted-foreground">{s.subjectName}</div>
                  <Button size="sm" className="mt-2" onClick={() => openLog(s)}>
                    {s.teachingLog ? 'Update log' : 'Record teaching'}
                  </Button>
                </Card>
              ))}
            </div>
          </>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-medium">
          <ListChecks className="h-4 w-4" />
          Syllabus coverage
        </div>
        <div className="flex flex-wrap gap-3">
          <Select
            label="Class"
            value={className}
            onChange={(e) => {
              setClassName(e.target.value)
              setSection('')
            }}
            options={[{ value: '', label: 'Select…' }, ...classSelectOptions(options)]}
          />
          {className ? (
            <Select
              label="Section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              options={[{ value: '', label: 'All / pick section' }, ...sectionSelectOptions(options, className)]}
            />
          ) : null}
          <TextField label="Subject" value={subjectName} onChange={(e) => setSubjectName(e.target.value)} />
        </div>
        {coverage.data && (
          <>
            <p className="text-sm text-muted-foreground">
              Weighted coverage {String(coverage.data.summary.coveragePercent)}% · {String(coverage.data.summary.completed)} completed ·{' '}
              {String(coverage.data.summary.partial)} partial · {String(coverage.data.summary.notStarted)} not started
            </p>
            <div className="hidden md:block overflow-x-auto">
              <DataTable columns={coverageColumns} rows={coverage.data.rows} getRowKey={(r) => r.topicId} />
            </div>
          </>
        )}
      </Card>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        size="lg"
        icon={ClipboardCheck}
        title="Record teaching"
        description={activeSlot ? `${classLabel(activeSlot.className, activeSlot.section)} · ${activeSlot.subjectName}` : undefined}
        footer={
          <>
            <Button onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saveLog.isPending} onClick={submitLog}>
              Save log
            </Button>
          </>
        }
      >
        <Select label="Execution status" value={periodOutcome} onChange={(e) => setPeriodOutcome(e.target.value)} options={OUTCOME_OPTIONS} />
        {periodOutcome !== 'not_conducted' && (
          <>
            <TextField label="Topic / content covered" value={topicTitle} onChange={(e) => setTopicTitle(e.target.value)} />
            <TextField
              label="Periods credited"
              type="number"
              min={1}
              value={String(periodsActual)}
              onChange={(e) => setPeriodsActual(Number(e.target.value) || 1)}
            />
          </>
        )}
        <TextArea label="Remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        {activeSlot?.lessonPlans?.length ? (
          <p className="text-sm text-muted-foreground">Linked lesson plan: {activeSlot.lessonPlans[0].title}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No lesson plan on file — you can still record teaching.</p>
        )}
      </Sheet>
    </Page>
  )
}

export default AcdmcTeaching
